<template>
  <v-container v-scroll="onScroll" class="px-3 px-sm-6 mt-2" fluid>
    <v-row class="mb-12">
      <v-col cols="12">
        <v-row class="align-end" no-gutters>
          <v-col cols="12" sm="8" lg="10">
            <h1 class="text-h4 font-weight-bold">
              <span v-if="isEliteVotedActive">Elite pool</span>
              <span v-else-if="isInspirationVotedActive">Inspiration pool</span>
              <span v-else>Prime Gallery</span>
            </h1>
            <h2 class="text-subtitle-1 mb-0">
              <span v-if="isEliteVotedActive">See photos proposed for the Elite gallery</span>
              <span v-else-if="isInspirationVotedActive">See photos proposed for the Inspiration gallery</span>
              <span v-else>See recently published photos curated by our community</span>
              <v-dialog v-if="!isEliteVotedActive && !isInspirationVotedActive" v-model="galleryDialog" width="400">
                <template #activator="{ on }">
                  <v-btn icon small class="ml-2" v-on="on">
                    <v-icon small>fal fa-info-circle</v-icon>
                  </v-btn>
                </template>
                <v-card outlined>
                  <v-card-title class="text-h5" primary-title>
                    What is the Prime Gallery?
                  </v-card-title>
                  <v-card-text>
                    <p>
                      If a photo submitted for curation reaches at least 80% of
                      popularity it will be published on the Prime gallery.
                    </p>
                    <p>
                      Having a photo published on this gallery is a great
                      achievement.
                    </p>
                    <p class="font-italic">
                      <small>
                        Photos in this gallery are eligible to be promoted to
                        the Elite gallery.
                      </small>
                    </p>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text small @click="galleryDialog = false">
                      Close
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </h2>
          </v-col>
          <v-col class="text-right" cols="12" sm="4" lg="2">
            <Filters
              :categories="categories"
              :on-apply="onApply"
              from="prime"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" class="px-0">
        <Gallery
          :photos="photos"
          :me="me"
          :load-more="loadMore"
          :auto-load-disabled="autoLoadDisabled"
          :loading="loading"
          :get-queries="getQueries"
          :is-elite-voted-active="isEliteVotedActive"
          :is-inspiration-voted-active="isInspirationVotedActive"
          from="prime"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment'
import goTo from 'vuetify/lib/services/goto'
import { mapState } from 'vuex'
import Gallery from '@/components/galleries/Gallery.vue'
import Filters from '@/components/galleries/Filters.vue'
import types from '@/utils/types'
import { EventBus } from '@/plugins/event-bus'

export default {
  components: {
    Gallery,
    Filters
  },
  middleware: 'header',
  data() {
    return {
      types,
      loading: false,
      sheet: false,
      tags: this.$route.query.tags,
      hasTags: !!this.$route.query.tags,
      offsetTop: 0,
      galleryDialog: false
    }
  },
  async fetch({ store, $asa }) {
    try {
      await store.dispatch('account/getCategories')
    } catch (error) {
      $asa.appInsights('Pages: prime.vue - method: fetch', error)
    }
  },
  head({ $seo }) {
    return $seo({
      title: 'Prime Gallery',
      description: 'Check the photos voted as the most popular by more than 80% of 100ASA community.'
    })
  },
  computed: {
    ...mapState({
      me: state => state.auth.user,
      photos: state => state.photos.list,
      isLastPage: state => state.photos.isLastPage,
      page: state => state.photos.page,
      categories: state => state.account.categories,
      selectedCategoryText: state => state.photos.selectedCategoryText,
      selectedTags: state => state.photos.selectedTags,
      loadDate: state => state.photos.galleryLoadDate,
      isEliteVotedActive: state => state.photos.isEliteVotedActive,
      isInspirationVotedActive: state => state.photos.isInspirationVotedActive
    }),
    autoLoadDisabled() {
      return this.loading || this.photos.length === 0
    }
  },
  async mounted() {
    const voteForEliteTrigger =
      this.$route.query.pool === 'elite' &&
      this.me &&
      (this.me.role === types.user.role.curator || this.me.isAdmin)

    const voteForInspirationTrigger =
      this.$route.query.pool === 'inspiration' &&
      this.me &&
      (this.me.role === types.user.role.curator || this.me.isAdmin)

    if (voteForEliteTrigger) {
      EventBus.$emit('set-vote-for-elite')
      this.$router.replace({ query: null })
    } else if (voteForInspirationTrigger) {
      EventBus.$emit('set-vote-for-inspiration')
      this.$router.replace({ query: null })
    } else if (
        this.$route.query.back === 'yes' &&
        this.$route.query.st &&
        this.photos.length > 0
      ) {
        goTo(Number(this.$route.query.st))
      } else {
        try {
          this.loading = true
          await this.$store.dispatch('photos/resetGallery')
          const data = {
            page: this.page,
            status: 'prime',
            back: !!this.$route.query.back,
            from: 'prime'
          }
          await this.$store.dispatch('photos/getPhotos', data)
          const date = new Date()
          await this.$store.dispatch('photos/setGalleryLoadDate', moment(date))
        } catch (error) {
          this.$asa.appInsights('Pages: prime.vue - method: mounted', error)
        } finally {
          this.loading = false
        }
      }
  },
  destroyed() {
    if (!this.$route.path.includes('/photo/')) {
      this.$store.dispatch('photos/resetGallery')
    }
  },
  methods: {
    async loadMore() {
      if (!this.isLastPage && !this.loading && this.photos.length !== 0) {
        this.loading = true
        try {
          await this.$store.dispatch('photos/getPhotos', {
            page: this.page + 1,
            status: 'prime',
            from: 'prime'
          })
        } catch (error) {
          this.$asa.appInsights('Pages: prime.vue - method: loadMore', error)
        } finally {
          this.loading = false
        }
      }
    },
    getQueries() {
      const q = this.$route.query
      let query = ''
      query += `&st=${this.offsetTop}`
      if (q.category) query += `&category=${q.category}`
      if (q.tags) query += `&tags=${q.tags}`
      return query
    },
    onScroll() {
      setTimeout(() => {
        const top = document.getElementById('app').getBoundingClientRect().top * -1
        this.offsetTop = top
      })
    },
    async onApply(filters) {
      await this.$store.dispatch('photos/resetGallery')
      await this.$store.dispatch('photos/setFilters', filters)
      const data = {
        page: this.page,
        status: 'prime',
        from: 'prime'
      }
      try {
        this.loading = true
        await this.$store.dispatch('photos/getPhotos', data)
        const date = new Date()
        await this.$store.dispatch('photos/setGalleryLoadDate', moment(date))
      } catch (error) {
        this.$asa.appInsights('Pages: prime.vue - method: onApply', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
